/**
 * Maps UI-layer state to Onboarding API request shapes.
 */
import { buildFilePart } from '../../platform/filePart';
import type { SurveyState } from '../../types/survey';
import type {
  ProfileInfoRequest,
  QuestionnaireRequest,
} from './onboardingTypes';
import {
  toOnboardingWeightUnit,
  toWeightKg,
} from '../../utils/weightUtils';
import {
  toOnboardingHeightUnit,
  normalizeHeightUnit,
  toFeetAndInches,
} from '../../utils/heightUtils';
import { toDateOnlyString } from '../../utils/dateHelpers';

const isLocalImageUri = (uri: string): boolean =>
  uri.startsWith('file:') ||
  uri.startsWith('content:') ||
  uri.startsWith('ph:') ||
  uri.startsWith('blob:');

const createProfilePicturePart = (uri: string, blob?: Blob | null): Blob => {
  let filename = uri.split('/').pop() || 'profile.jpg';
  if (!filename.includes('.')) {
    filename = 'profile.jpg';
  }
  const ext = filename.split('.').pop()?.toLowerCase();
  const type = ext === 'png' ? 'image/png' : 'image/jpeg';
  return buildFilePart({ uri, name: filename, type, blob });
};

// ─── ProfileInfo mapper ───────────────────────────────────────────────────────

export interface BasicInfoFormState {
  gender: string;
  /** Numeric ID from the COUNTRIES list. The backend accepts the country id, not the ISO code. */
  countryId: number;
  heightMetres: number;
  heightUnit: string;
  weight: number;
  weightUnit: string;
  /** Calendar DOB as `Date` or `YYYY-MM-DD` — never rely on UTC ISO for this field. */
  dob: Date | string;
  profilePictureUri?: string;
  /** Web only: the picked File/Blob, which browsers require in FormData. */
  profilePictureBlob?: Blob | null;
  userGUID: string;
}

/**
 * Builds multipart/form-data for POST Registration/OnboardingProfileInfo.
 *
 * Sends the values the user entered in their selected units.
 * Backend converts to canonical storage; client only maps unit enums:
 * - HeightUnit = "HeightCM" | "HeightFT"
 * - WeightUnit = "WeightKG" | "WeightLBS"
 */
export const mapBasicInfoToProfileInfoRequest = (
  state: BasicInfoFormState,
): FormData => {
  const heightUnit = toOnboardingHeightUnit(state.heightUnit);
  const weightUnit = toOnboardingWeightUnit(state.weightUnit);

  // Selected height value (no client-side ft→cm conversion).
  // Metric UI stores metres → send cm when HeightCM.
  // Imperial UI stores metres internally → restore ft.in (5.8 = 5'8") for HeightFT.
  let heightValue: number;
  if (normalizeHeightUnit(state.heightUnit) === 'ft') {
    const { ft, in: inches } = toFeetAndInches(state.heightMetres);
    heightValue = Number((ft + inches / 10).toFixed(1));
  } else {
    heightValue = Number((state.heightMetres * 100).toFixed(2));
  }

  // Selected weight value (no client-side lbs→kg conversion).
  const weightValue = Number(Number(state.weight).toFixed(2));

  const formData = new FormData();
  formData.append('UserGUID', state.userGUID);
  formData.append('Gender', state.gender as ProfileInfoRequest['gender']);
  formData.append('CountryID', String(state.countryId));
  formData.append('HeightCM', String(heightValue));
  formData.append('HeightUnit', heightUnit);
  formData.append('WeightKG', String(weightValue));
  formData.append('WeightUnit', weightUnit);
  // Calendar date only — `toISOString()` shifts the day across timezones.
  formData.append('DateOfBirth', toDateOnlyString(state.dob));

  const pictureUri = state.profilePictureUri?.trim();
  const hasProfilePicture = !!(pictureUri && isLocalImageUri(pictureUri));
  if (hasProfilePicture && pictureUri) {
    formData.append(
      'ProfilePicture',
      createProfilePicturePart(pictureUri, state.profilePictureBlob),
    );
  }

  return formData;
};

// ─── Questionnaire mapper ─────────────────────────────────────────────────────


const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * 1-based ordinals of the backend enums (see swagger `MainGoal`,
 * `CommitmentLevel`, `CheckinFrequency`). Keys are the values the survey UIs
 * store; `BuildMuscle` is mobile's legacy label for `StayConsistent`.
 */
const MAIN_GOAL_MAP: Record<string, number> = {
  LoseWeight: 1,
  loseWeight: 1,
  StayConsistent: 2,
  stayConsistent: 2,
  BuildMuscle: 2,
  GetHealthier: 3,
  getHealthier: 3,
  MaintainWeight: 4,
  maintainWeight: 4,
};

const COMMITMENT_LEVEL_MAP: Record<string, number> = {
  GetStarted: 3,
  getting_started: 3,
  PrettyCommitted: 2,
  pretty_committed: 2,
  VeryCommitted: 1,
  very_committed: 1,
};

const CHECK_IN_FREQUENCY_MAP: Record<string, number> = {
  daily: 1,
  Daily: 1,
  fewTimes: 2,
  FewTimesWeek: 2,
  onceWeek: 3,
  OnceWeek: 3,
};

const PREFERRED_BUDDY_GENDER_MAP: Record<string, number> = {
  none: 1,
  noPreference: 1,
  NoPreference: 1,
  male: 2,
  Male: 2,
  female: 3,
  Female: 3,
  other: 4,
  Other: 4,
};

const PREFERRED_BUDDY_TYPE_MAP: Record<string, number> = {
  Mentor: 1,
  mentor: 1,
  Partner: 2,
  partner: 2,
  Cheerleader: 3,
  cheerleader: 3,
};

const COMMUNICATION_STYLE_MAP: Record<string, number> = {
  Supportive: 1,
  supportive: 1,
  Balanced: 2,
  balanced: 2,
  Direct: 3,
  direct: 3,
};

/**
 * Clamps a user-selected rating to the valid 1–10 range.
 * Returns 0 when the user has not selected a rating (null/undefined).
 */
const toRating = (value: number | null | undefined): number => {
  if (value == null || !Number.isFinite(value)) return 0;
  return clamp(Math.round(value), 1, 10);
};

export const mapSurveyToQuestionnaireRequest = (
  survey: SurveyState,
  userGUID: string,
): QuestionnaireRequest => {
  const ratings = survey.personalizationRatings ?? {};
  const ageRange = survey.ageRange;

  const checkInImportance = ratings.checkInImportance ?? ratings.checkInFrequency;
  const consistency = ratings.consistency ?? ratings.consistentHealthyHabit;
  const motivation = ratings.motivation ?? ratings.motivationToWeightLoss;
  const sharingHonesty = ratings.sharingHonesty ?? ratings.progressSharing;

  return {
    userGUID,
    checkInFrequency: CHECK_IN_FREQUENCY_MAP[survey.checkInFrequency!] || 1,
    commitmentLevel: COMMITMENT_LEVEL_MAP[survey.commitmentLevel!] || 1,
    communicationStyle: COMMUNICATION_STYLE_MAP[survey.communicationStyle!] || 1,
    mainGoal: MAIN_GOAL_MAP[survey.mainGoal!] || 1,
    preferredBuddyAgeMin: clamp(ageRange?.min || 18, 13, 120),
    preferredBuddyAgeMax: clamp(ageRange?.max || 65, 13, 120),
    preferredBuddyGender: PREFERRED_BUDDY_GENDER_MAP[survey.genderPreference!] || 1,
    preferredBuddyType: PREFERRED_BUDDY_TYPE_MAP[survey.buddyType!] || 1,
    targetWeightLossKG: survey.poundsToLose != null ? toWeightKg(survey.poundsToLose, 'lbs') : 0.1,
    // Ratings are optional — send 0 when the user has not selected a value (see toRating).
    ratingAccountability: toRating(ratings.accountability as number | null),
    ratingCheckInFrequency: toRating(checkInImportance as number | null),
    ratingConsistentHealthyHabit: toRating(consistency as number | null),
    ratingEmotionalSupportImportance: toRating(ratings.emotionalSupport as number | null),
    ratingMotivationToWeightLoss: toRating(motivation as number | null),
    ratingProgressSharingComfortLevel: toRating(sharingHonesty as number | null),
  };
};

export const REVERSE_MAIN_GOAL_MAP: Record<number, string> = {
  1: 'LoseWeight',
  2: 'BuildMuscle', // mapped back to legacy mobile value for survey UI
  3: 'GetHealthier',
  4: 'MaintainWeight',
};

export const REVERSE_COMMITMENT_LEVEL_MAP: Record<number, string> = {
  1: 'very_committed',
  2: 'pretty_committed',
  3: 'getting_started',
};

export const REVERSE_CHECK_IN_FREQUENCY_MAP: Record<number, string> = {
  1: 'daily',
  2: 'fewTimes',
  3: 'onceWeek',
};

export const REVERSE_PREFERRED_BUDDY_GENDER_MAP: Record<number, string> = {
  1: 'noPreference',
  2: 'male',
  3: 'female',
  4: 'other',
};

export const REVERSE_PREFERRED_BUDDY_TYPE_MAP: Record<number, string> = {
  1: 'mentor',
  2: 'partner',
  3: 'cheerleader',
};

export const REVERSE_COMMUNICATION_STYLE_MAP: Record<number, string> = {
  1: 'supportive',
  2: 'balanced',
  3: 'direct',
};

export const mapPreferencesToSurveyState = (
  prefs: import('../../types/userProfile').GetUserBuddyPreferencesResponse
): SurveyState => {
  return {
    mainGoal: prefs.mainGoalId ? REVERSE_MAIN_GOAL_MAP[prefs.mainGoalId] || null : null,
    poundsToLose: prefs.targetWeightLossKg ? Number((prefs.targetWeightLossKg * 2.20462).toFixed(2)) : null, // kg to lbs
    commitmentLevel: prefs.commitmentLevelId ? REVERSE_COMMITMENT_LEVEL_MAP[prefs.commitmentLevelId] || null : null,
    buddyType: prefs.preferredBuddyTypeId ? REVERSE_PREFERRED_BUDDY_TYPE_MAP[prefs.preferredBuddyTypeId] || null : null,
    communicationStyle: prefs.communicationStyleId ? REVERSE_COMMUNICATION_STYLE_MAP[prefs.communicationStyleId] || null : null,
    checkInFrequency: prefs.checkInFrequencyId ? REVERSE_CHECK_IN_FREQUENCY_MAP[prefs.checkInFrequencyId] || null : null,
    genderPreference: prefs.preferredBuddyGenderId ? REVERSE_PREFERRED_BUDDY_GENDER_MAP[prefs.preferredBuddyGenderId] || null : null,
    ageRange:
      prefs.preferredBuddyAgeMin && prefs.preferredBuddyAgeMax
        ? { min: prefs.preferredBuddyAgeMin, max: prefs.preferredBuddyAgeMax }
        : null,
    personalizationRatings: {
      accountability: prefs.ratingAccountability ?? null,
      checkInImportance: prefs.ratingCheckInFrequency ?? null,
      consistency: prefs.ratingConsistentHealthyHabit ?? null,
      emotionalSupport: prefs.ratingEmotionalSupportImportance ?? null,
      motivation: prefs.ratingMotivationToWeightLoss ?? null,
      sharingHonesty: prefs.ratingProgressSharingComfortLevel ?? null,
    },
  };
};

export const BUDDY_PREFERENCES_UPDATED_EVENT = 'BUDDY_PREFERENCES_UPDATED' as const;

/**
 * Maps survey answers into the GetUserBuddyPreferencesResponse structure.
 */
export const mapSurveyToBuddyPreferences = (
  survey: SurveyState,
  userGuid = '',
  userId = 0,
): import('../../types/userProfile').GetUserBuddyPreferencesResponse => {
  const req = mapSurveyToQuestionnaireRequest(survey, userGuid);
  return {
    statusCode: 200,
    message: 'User buddy preference details fetched successfully.',
    success: true,
    userBuddyPreferenceId: 0,
    userId,
    userGuid,
    mainGoalId: req.mainGoal,
    targetWeightLossKg: req.targetWeightLossKG,
    commitmentLevelId: req.commitmentLevel,
    preferredBuddyTypeId: req.preferredBuddyType,
    communicationStyleId: req.communicationStyle,
    checkInFrequencyId: req.checkInFrequency,
    preferredBuddyGenderId: req.preferredBuddyGender,
    preferredBuddyAgeMin: req.preferredBuddyAgeMin,
    preferredBuddyAgeMax: req.preferredBuddyAgeMax,
    ratingMotivationToWeightLoss: req.ratingMotivationToWeightLoss,
    ratingConsistentHealthyHabit: req.ratingConsistentHealthyHabit,
    ratingAccountability: req.ratingAccountability,
    ratingProgressSharingComfortLevel: req.ratingProgressSharingComfortLevel,
    ratingCheckInFrequency: req.ratingCheckInFrequency,
    ratingEmotionalSupportImportance: req.ratingEmotionalSupportImportance,
  };
};
